import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import fieldNames from '../constants/field-names';
import getDownloadOptions from '../helpers/get-download-options';
import createDownloadValidator from '../validators/create-download-validator';
import createDownloadCustomPathValidator from '../validators/create-download-custom-path-validator';

const {
  Box,
  Stack,
  FilePickerField,
  RadioButtonGroupField,
  CheckboxField,
  InputGroupField,
  Loader,
  FieldStatus,
  Mark,
} = designSystem;
const { useWatch, useFormState } = reactHookForm;

const DownloadField = ({
  control,
  downloadCustomPath,
  getFormValues,
  setFormValue,
  clearFormErrors,
  onChange,
  onClick,
}) => {
  const downloadOptions = getDownloadOptions();

  const { errors } = useFormState({ control });
  const fileName = useWatch({ control, name: fieldNames.DOWNLOAD_FILE_NAME });

  const referenceValue = getFormValues(fieldNames.DOWNLOAD_REFERENCE);
  const fileNameValue = getFormValues(fieldNames.DOWNLOAD_FILE_NAME);
  const fileValue = getFormValues(fieldNames.DOWNLOAD_FILE);
  const shouldUseValidator = (referenceValue && !fileNameValue) || (!referenceValue && !fileValue);
  const downloadCustomPathError = errors[fieldNames.DOWNLOAD_CUSTOM_PATH];

  const handleFileChange = useCallback(
    (fileSet) => {
      const file = fileSet[0];

      clearFormErrors(fieldNames.DOWNLOAD_FILE);
      setFormValue(fieldNames.DOWNLOAD_FILE_NAME, file.name);

      onChange({ fieldName: fieldNames.DOWNLOAD_FILE, fieldValue: file });
      onChange({
        fieldName: fieldNames.DOWNLOAD_FILE_NAME,
        fieldValue: file.name,
      });
      downloadCustomPath.handlers.enableCustomPath();
    },
    [clearFormErrors, setFormValue, onChange, downloadCustomPath.handlers]
  );

  const handleFileClear = useCallback(() => {
    setFormValue(fieldNames.DOWNLOAD_FILE, null, { shouldDirty: true });
    setFormValue(fieldNames.DOWNLOAD_FILE_NAME, '', { shouldDirty: true });

    onChange({ fieldName: fieldNames.DOWNLOAD_FILE, fieldValue: null });
    onChange({ fieldName: fieldNames.DOWNLOAD_FILE_NAME, fieldValue: '' });

    downloadCustomPath.handlers.enableCustomPath();
  }, [onChange, setFormValue, downloadCustomPath.handlers]);

  const handleDownloadOptionChange = useCallback(
    (value) => {
      onChange({ fieldName: fieldNames.DOWNLOAD_OPTION, fieldValue: value });
    },
    [onChange]
  );

  const handleCustomPathChange = useCallback(
    (value) => {
      onChange({ fieldName: fieldNames.DOWNLOAD_CUSTOM_PATH, fieldValue: value });
    },
    [onChange]
  );

  const handleToggleCustomPath = useCallback(
    (checked) => {
      downloadCustomPath.handlers.toggleCustomPathField();
      onChange({ fieldName: fieldNames.DOWNLOAD_CUSTOM_PATH_SWITCHER, fieldValue: checked });
    },
    [downloadCustomPath.handlers, onChange]
  );

  const handleFileClick = useCallback(() => {
    onClick({ fieldName: fieldNames.DOWNLOAD_FILE });
  }, [onClick]);

  return (
    <Stack gap="spacing-2-xs">
      <FilePickerField
        name={fieldNames.DOWNLOAD_FILE}
        control={control}
        {...(shouldUseValidator && {
          rules: {
            validate: createDownloadValidator(),
          },
        })}
        label={i18next.t('Choose file')}
        fileItemName={fileName}
        placeholder={i18next.t('No file chosen')}
        onChange={handleFileChange}
        onClear={handleFileClear}
        onClick={handleFileClick}
      />

      <RadioButtonGroupField
        name={fieldNames.DOWNLOAD_OPTION}
        control={control}
        options={downloadOptions}
        onChange={handleDownloadOptionChange}
      />

      {downloadCustomPath.state.isLoading && (
        <Box marginTop="spacing-m">
          <Loader size="medium" />
        </Box>
      )}

      {!downloadCustomPath.state.isLoading && (
        <Stack gap="spacing-3-xs">
          <CheckboxField
            name={fieldNames.DOWNLOAD_CUSTOM_PATH_SWITCHER}
            control={control}
            label={i18next.t('Set Download URL')}
            disabled={downloadCustomPath.state.disabled}
            onChange={handleToggleCustomPath}
          />
          {downloadCustomPath.state.visible && (
            <div>
              <InputGroupField
                name={fieldNames.DOWNLOAD_CUSTOM_PATH}
                control={control}
                rules={{
                  validate: createDownloadCustomPathValidator(),
                }}
                iconGlyph="link"
                disabled={downloadCustomPath.state.disabled}
                onChange={handleCustomPathChange}
              />
              {!downloadCustomPathError && (
                <FieldStatus
                  text={
                    <>
                      {`${i18next.t('The URL will look like this:')} `}

                      <span style={{ whiteSpace: 'nowrap' }}>
                        yourdomain.com
                        <strong>
                          <Mark appearance="high-emphasis">/your-url</Mark>
                        </strong>
                        .
                      </span>

                      {i18next.t('To publish under a different URL, re-upload the file')}
                    </>
                  }
                />
              )}
            </div>
          )}
        </Stack>
      )}
    </Stack>
  );
};

DownloadField.propTypes = {
  control: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  downloadCustomPath: PropTypes.shape({
    state: PropTypes.shape({
      disabled: PropTypes.bool.isRequired,
      visible: PropTypes.bool.isRequired,
      isLoading: PropTypes.bool.isRequired,
    }).isRequired,
    handlers: PropTypes.shape({
      toggleCustomPathField: PropTypes.func.isRequired,
      enableCustomPath: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  getFormValues: PropTypes.func.isRequired,
  setFormValue: PropTypes.func.isRequired,
  clearFormErrors: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default DownloadField;
